import axios from 'axios';
import axiosInstance from '../axiosInstance';

/**
 * 2days Ops 콘솔 (운영진 ROLE_A) — Setup 단계별 저장.
 * 응답은 모두 프로젝트 Setup 전체(ProjectDetail)라 화면은 받은 값으로 바로 바꾼다.
 */
const data = (res) => res.data?.data ?? res.data;
const base = '/two-days/admin/projects';

export const getOpsProjects = async () => data(await axiosInstance.get(base));
export const getOpsProject = async (id) => data(await axiosInstance.get(`${base}/${id}`));
export const createOpsProject = async (body) => data(await axiosInstance.post(base, body));
export const saveOpsProject = async (id, body) =>
  data(await axiosInstance.put(`${base}/${id}`, body));
export const saveOpsBrief = async (id, body) =>
  data(await axiosInstance.put(`${base}/${id}/brief`, body));
export const createOpsTeam = async (id, name) =>
  data(await axiosInstance.post(`${base}/${id}/teams`, { name }));
/** 스태프 계정 — 받을 직무의 팀원이 없을 때 자동으로 팀원이 되는 운영진 계정 */
export const getOpsStaff = async () => data(await axiosInstance.get('/two-days/admin/staff'));
export const addOpsStaff = async (body) =>
  data(await axiosInstance.post('/two-days/admin/staff', body));
export const deleteOpsStaff = async (id) =>
  data(await axiosInstance.delete(`/two-days/admin/staff/${id}`));

/** Team & roles 에서 고를 파일럿 신청자 (이 프로젝트에서 들어간 팀 포함) */
export const getOpsApplicants = async (id) =>
  data(await axiosInstance.get(`${base}/${id}/applicants`));
export const saveOpsTeams = async (id, teams) =>
  data(await axiosInstance.put(`${base}/${id}/teams`, { teams }));
export const saveOpsTasks = async (id, tasks) =>
  data(await axiosInstance.put(`${base}/${id}/tasks`, { tasks }));
export const saveOpsCheckpoints = async (id, checkpoints) =>
  data(await axiosInstance.put(`${base}/${id}/checkpoints`, { checkpoints }));
export const startOpsProject = async (id) => data(await axiosInstance.post(`${base}/${id}/start`));
export const endOpsProject = async (id) => data(await axiosInstance.post(`${base}/${id}/end`));

/** Lead inbox — 모든 프로젝트·팀에서 받는 사람이 팀장인 제출 태스크 */
export const getOpsInbox = async () => data(await axiosInstance.get('/two-days/admin/inbox'));
export const getOpsInboxItem = async (taskId) =>
  data(await axiosInstance.get(`/two-days/admin/inbox/${taskId}`));
/** approve · reject · return · score · comment — 처리 후 상세를 돌려준다 */
export const postOpsInboxAction = async (taskId, action) =>
  data(await axiosInstance.post(`/two-days/admin/inbox/${taskId}/actions`, action));

/** 시작 뒤 운영진 변경 — 진행 중 워크보드 태스크 (재배정 · 받는 사람 · 마감 · 취소) */
export const getOpsLiveTasks = async (id) =>
  data(await axiosInstance.get(`${base}/${id}/live-tasks`));
export const editOpsLiveTask = async (id, taskId, body) =>
  data(await axiosInstance.post(`${base}/${id}/live-tasks/${taskId}`, body));
/** 팀원 이탈 판정 (dropped) · 되돌리기 (active) */
export const setOpsMemberStatus = async (id, memberId, body) =>
  data(await axiosInstance.post(`${base}/${id}/members/${memberId}/status`, body));

/** Ops 알림 — Lead inbox 인계 · 팀장에게 온 질문·답글 · 태스크 거절 */
export const getOpsNotifications = async () =>
  data(await axiosInstance.get('/two-days/admin/notifications'));
export const readOpsNotification = async (notificationId) =>
  data(await axiosInstance.post(`/two-days/admin/notifications/${notificationId}/read`));
export const readAllOpsNotifications = async () =>
  data(await axiosInstance.post('/two-days/admin/notifications/read-all'));
/** 참여자가 보낸 운영진 메시지 — 답장하면 보낸 사람에게만 알림이 간다 */
export const getOpsMessages = async () =>
  data(await axiosInstance.get('/two-days/admin/messages', { skipLoading: true }));
export const replyOpsMessage = async (messageId, body) =>
  data(await axiosInstance.post(`/two-days/admin/messages/${messageId}/reply`, { body }));
/** 선발 안내 메일 다시 보내기 — 24시간 응답을 보낸 시각부터 다시 잰다 */
export const resendOpsInvite = async (id, memberId) =>
  data(await axiosInstance.post(`${base}/${id}/members/${memberId}/invite`));

/** Growth Report — 참여자별 완주 여부·리포트 상태 목록, 뷰어 머리말, 상태(Confirm · Sent · Send failed) */
export const getOpsReports = async (id) => data(await axiosInstance.get(`${base}/${id}/reports`));
/** 피어리뷰 응답 보기 — 쓴 것(given) · 받은 것(received) 원문 (Ops 전용 · 읽기 전용) */
export const getOpsPeerReview = async (memberId) =>
  data(await axiosInstance.get(`/two-days/admin/reports/${memberId}/peer-review`));
export const getOpsReport = async (memberId) =>
  data(await axiosInstance.get(`/two-days/admin/reports/${memberId}`));
export const setOpsReportStatus = async (memberId, body) =>
  data(await axiosInstance.post(`/two-days/admin/reports/${memberId}/status`, body));

/** AI 초안 만들기 (섹션 2 요약 · 섹션 6) — 오래 걸릴 수 있다 (최대 ~40초) */
export const generateOpsReportAi = async (memberId) =>
  data(
    await axiosInstance.post(`/two-days/admin/reports/${memberId}/ai`, null, { timeout: 60000 }),
  );

/** 리포트 메일 — PDF 를 붙여 바로 보낸다. 결과 상태(Sent / Send failed)가 담긴 머리말을 돌려준다 */
export const sendOpsReportEmail = async (memberId, { to, kind, message, pdf, filename }) => {
  const form = new FormData();
  form.append('to', to);
  form.append('kind', kind);
  form.append('message', message);
  form.append('pdf', pdf, filename);
  return data(
    await axiosInstance.post(`/two-days/admin/reports/${memberId}/email`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }),
  );
};

/** Team files (Resources) — 처리 후 목록 전체를 돌려준다. Project brief 가 맨 위 */
export const getOpsFiles = async (id) => data(await axiosInstance.get(`${base}/${id}/files`));
/** 기본 Resources 중 빠진 것만 넣는다 (같은 이름이 있으면 건너뜀) */
export const addOpsDefaultFiles = async (id) =>
  data(await axiosInstance.post(`${base}/${id}/files/defaults`));
export const addOpsFile = async (id, body) =>
  data(await axiosInstance.post(`${base}/${id}/files`, body));
export const updateOpsFile = async (id, fileId, body) =>
  data(await axiosInstance.put(`${base}/${id}/files/${fileId}`, body));
export const removeOpsFile = async (id, fileId) =>
  data(await axiosInstance.delete(`${base}/${id}/files/${fileId}`));
/** 올리기 주소를 받아 S3 에 올리고 { key, fileName, size } 를 돌려준다 */
const uploadTo = async (urlPath, file) => {
  const { uploadUrl, key } = data(
    await axiosInstance.post(urlPath, {
      fileName: file.name,
      size: file.size,
    }),
  );
  await axios.put(uploadUrl, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  return { key, fileName: file.name, size: file.size };
};

/** 파일을 S3 에 올리고 { key, fileName, size } 를 돌려준다 */
export const uploadOpsFile = async (id, file) => uploadTo(`${base}/${id}/files/upload-url`, file);

/**
 * 기본 Resources — 운영진이 직접 넣는 것. 이후 만드는 프로젝트와 Add default files 에 복사된다.
 * 처리 후 목록 전체를 돌려준다 (Team files 와 같은 모양)
 */
const defaultsBase = '/two-days/admin/default-files';
export const getDefaultFiles = async () => data(await axiosInstance.get(defaultsBase));
export const addDefaultFile = async (body) => data(await axiosInstance.post(defaultsBase, body));
export const updateDefaultFile = async (fileId, body) =>
  data(await axiosInstance.put(`${defaultsBase}/${fileId}`, body));
export const removeDefaultFile = async (fileId) =>
  data(await axiosInstance.delete(`${defaultsBase}/${fileId}`));
export const uploadDefaultFile = async (file) => uploadTo(`${defaultsBase}/upload-url`, file);

/** Live board — 모든 팀 워크보드(읽기 전용) + 동료 평가 + Ops 기록. 행동 뒤에는 보드 전체를 돌려준다 */
export const getOpsLiveBoard = async (id) =>
  data(await axiosInstance.get(`${base}/${id}/live-board`, { skipLoading: true }));
export const postOpsLiveBoardAction = async (id, action) =>
  data(await axiosInstance.post(`${base}/${id}/live-board/actions`, action));

/** Ops 메신저 — 팀 › 팀원 명단 전체와 대화. send · read · typing · resolve */
export const getOpsChat = async (id) =>
  data(await axiosInstance.get(`${base}/${id}/chat`, { skipLoading: true, skipGlobalError: true }));
export const getOpsChatUnread = async () =>
  data(
    await axiosInstance.get('/two-days/admin/chat/unread', {
      skipLoading: true,
      skipGlobalError: true,
    }),
  );
export const postOpsChatAction = async (id, memberId, body) =>
  data(
    await axiosInstance.post(`${base}/${id}/chat/${memberId}`, body, {
      skipLoading: true,
      skipGlobalError: true,
    }),
  );
